"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ssoClient } from "@sadr-hub/api";
import { IgAccount, SocialAccount, TkAccount } from "@/../../packages/utils/src/global";
import { useAuth } from "./AuthContext";

interface AccountContextType {
  accounts: SocialAccount[];
  selectedAccount: SocialAccount | null;
  setSelectedAccount: (account: SocialAccount) => void;
  setSelectedAccountById: (id: string) => void;
  loading: boolean;
  refreshAccounts: () => Promise<void>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

const ACCOUNTS_QUERY_KEY = ["dashboard", "accounts"];

export function AccountProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedAccount, setSelectedAccount] =
    useState<SocialAccount | null>(null);

  const { isAuthenticated, user } = useAuth();

  const {
    data: accounts = [],
    isLoading,
    refetch,
  } = useQuery<SocialAccount[]>({
    queryKey: ACCOUNTS_QUERY_KEY,

    queryFn: async () => {
      const response = await ssoClient.get("/dashboard/accounts");
    
      return [
        ...(response.data?.igAccounts ?? []),
        ...(response.data?.tkAccounts ?? []),
      ];
    },

    // Don't refetch every time AccountProvider mounts
    staleTime: 5 * 60 * 1000,

    // Keep cached data in memory for 30 minutes
    gcTime: 30 * 60 * 1000,

    // Don't refetch just because window gets focus
    refetchOnWindowFocus: false,

    // Don't automatically retry unnecessarily
    retry: 10,

    enabled: !!user && isAuthenticated,
  });

  /**
   * Recover selected account whenever accounts are available.
   */
  // useEffect(() => {
  //   if (!accounts.length) {
  //     setSelectedAccount(null);
  //     return;
  //   }

  //   // const savedAccountId = localStorage.getItem(
  //   //   "selected_instagram_account_id"
  //   // );
  //  const savedAccountId = localStorage.getItem(
  //     "selected_instagram_account_id"
  //   );
  //   const matchedAccount = accounts.find(
  //     (acc:any) => acc.igUserId.toString() === savedAccountId
  //   );

  //   if (matchedAccount) {
  //     setSelectedAccount(matchedAccount);
  //   } else {
  //     const firstAccount = accounts[0];

  //     setSelectedAccount(firstAccount);

  //     localStorage.setItem(
  //       "selected_instagram_account_id",
  //       firstAccount.igUserId.toString()
  //     );
  //   }
  // }, [accounts]);

  useEffect(() => {
    if (!accounts.length) {
      setSelectedAccount(null);
      return;
    }
  
    const savedAccountId = localStorage.getItem("selected_account_id");
  
    const matchedAccount = accounts.find((account: SocialAccount) => {
      const accountId =
        account.type === "instagram"
          ? (account as IgAccount).igUserId
          : (account as TkAccount).tkUserId;
  
      return `${account.type}:${accountId}` === savedAccountId;
    });
  
    if (matchedAccount) {
      setSelectedAccount(matchedAccount);
    } else {
      const firstAccount = accounts[0];
  
      setSelectedAccount(firstAccount);
  
      const accountId =
        firstAccount.type === "instagram"
          ? (firstAccount as IgAccount).igUserId
          : (firstAccount as TkAccount).tkUserId;
  
      localStorage.setItem(
        "selected_account_id",
        `${firstAccount.type}:${accountId}`
      );
    }
  }, [accounts]);


  const handleSetSelectedAccount = (account: SocialAccount) => {
    setSelectedAccount(account);
    const accountId =
    account.type === "instagram"
      ? (account as IgAccount).igUserId
      : (account as TkAccount).tkUserId;
    localStorage.setItem(
      "selected_account_id",
      `${account.type}:${accountId}`
    );
  };
  // const handleSetSelectedAccount = (account: IgAccount) => {
  //   setSelectedAccount(account);

  //   localStorage.setItem(
  //     "selected_instagram_account_id",
  //     account.igUserId.toString()
  //   );
  // };

  const setSelectedAccountById = (
    id: string | null | undefined
  ) => {
    if (!id) return;
    const account = accounts.find(
      (a:SocialAccount) =>
        a.type === "instagram" ? (a as IgAccount).igUserId === id : 
        (a  as TkAccount).tkUserId === id 
    );

    if (!account) return;

    setSelectedAccount(account);
    localStorage.setItem(
      "selected_account_id",
      `${account.type}:${id}`
    );
  };
  // const setSelectedAccountByIgId = (
  //   igId: string | null | undefined
  // ) => {
  //   if (!igId) return;
  //   const account = accounts.find(
  //     (a:any) => a.igUserId.toString() === igId.toString()
  //   );

  //   if (!account) return;

  //   setSelectedAccount(account);

  //   localStorage.setItem(
  //     "selected_instagram_account_id",
  //     account.igUserId.toString()
  //   );
  // };

  const refreshAccounts = async () => {
    await refetch();
  };

  return (
    <AccountContext.Provider
      value={{
        accounts,
        selectedAccount,
        setSelectedAccount: handleSetSelectedAccount,
        setSelectedAccountById,
        loading: isLoading,
        refreshAccounts,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);

  if (!context) {
    throw new Error(
      "useAccount must be used within an AccountProvider"
    );
  }

  return context;
}